import React, { useCallback, useState } from "react";

import { pickRowValue } from "../../utils/tableColumnValue";

/**
 * Presentational server-style table: optional Actions column, optional prepend columns (React),
 * then data columns from `columnDefs` + `visibility`.
 *
 * Use with {@link ./AdminServerList.jsx} for paging/sort/fetch, or standalone on seller/buyer pages.
 *
 * @param {object[]} props.columnDefs `{ key, label, aliases?, html?, defaultVisible?, sortable?, renderCell?, thClassName?, tdClassName? }`
 * @param {Record<string, boolean>} props.visibility
 * @param {object[]} props.rows
 * @param {boolean} props.loading
 * @param {string} props.emptyText
 * @param {(row: object) => React.ReactNode} [props.renderActions] If omitted, no Actions column.
 * @param {{ id: string, header: React.ReactNode, headerTitle?: string, thClassName?: string, tdClassName?: string, renderCell: (row: object) => React.ReactNode }[]} [props.prependColumns] Extra columns after Actions (e.g. alerts).
 * @param {(dragKey: string, dropKey: string) => void} [props.onColumnReorder] When set, data columns show a drag handle; order is persisted by the parent (e.g. {@link ../../hooks/useFreightAdminTablePrefs.js}).
 * @param {string} [props.wrapperClassName]
 * @param {string|null} [props.sortBy]
 * @param {'asc'|'desc'} [props.sortDir]
 * @param {(key: string) => void} [props.onSortColumn]
 * @param {(row: object) => string} [props.getRowClassName] Extra `className` on each body row (e.g. status highlight).
 * @param {string} [props.tableClassName="admin-dt-table"] Class on `<table>`.
 */
const AdminDataTable = ({
  columnDefs,
  visibility,
  rows,
  loading,
  emptyText,
  renderActions,
  prependColumns = [],
  onColumnReorder,
  footer = null,
  wrapperClassName = "table-scroll admin-dt-scroll",
  sortBy = null,
  sortDir = "asc",
  onSortColumn,
  getRowClassName,
  tableClassName = "admin-dt-table"
}) => {
  const visibleDefs = columnDefs.filter((d) => visibility[d.key] !== false);
  const hasActions = typeof renderActions === "function";
  const leadCount = (hasActions ? 1 : 0) + prependColumns.length;
  const colCount = leadCount + visibleDefs.length;
  const sortable = typeof onSortColumn === "function";
  const reorderable = typeof onColumnReorder === "function";

  const [dragOverKey, setDragOverKey] = useState(null);

  const handleDragStart = useCallback((e, columnKey) => {
    e.dataTransfer.setData("text/plain", columnKey);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("application/x-admin-dt-column", columnKey);
    } catch {
      /* ignore */
    }
  }, []);

  const handleDragOver = useCallback(
    (e, columnKey) => {
      if (!reorderable) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setDragOverKey(columnKey);
    },
    [reorderable]
  );

  const handleDragLeave = useCallback(() => {
    setDragOverKey(null);
  }, []);

  const handleDrop = useCallback(
    (e, dropKey) => {
      if (!reorderable) return;
      e.preventDefault();
      setDragOverKey(null);
      const dragKey =
        e.dataTransfer.getData("application/x-admin-dt-column") || e.dataTransfer.getData("text/plain");
      if (dragKey && dragKey !== dropKey) onColumnReorder(dragKey, dropKey);
    },
    [onColumnReorder, reorderable]
  );

  const handleDragEnd = useCallback(() => {
    setDragOverKey(null);
  }, []);

  const renderDragHandle = (d) => {
    if (!reorderable) return null;
    return (
      <span
        className="admin-dt-col-drag"
        draggable
        onDragStart={(e) => handleDragStart(e, d.key)}
        onDragEnd={handleDragEnd}
        title="Drag to reorder column"
        aria-label={`Drag to reorder ${d.label} column`}
        role="presentation"
      >
        <i className="fas fa-grip-vertical text-gray-400 text-xs" aria-hidden />
      </span>
    );
  };

  const thDropProps = (d) =>
    reorderable
      ? {
          onDragOver: (e) => handleDragOver(e, d.key),
          onDragLeave: handleDragLeave,
          onDrop: (e) => handleDrop(e, d.key)
        }
      : {};

  const thClassForData = (d) =>
    ["admin-dt-th", d.thClassName, dragOverKey === d.key ? "admin-dt-th--drop-target" : ""]
      .filter(Boolean)
      .join(" ");

  const renderSortableTh = (d) => {
    const drag = renderDragHandle(d);
    const innerRowClass = "admin-dt-th-inner flex flex-row flex-nowrap items-center gap-1.5 min-w-0";

    if (!sortable || d.html || d.sortable === false) {
      return (
        <th key={d.key} className={thClassForData(d)} {...thDropProps(d)}>
          <div className={innerRowClass}>
            {drag}
            {d.label}
          </div>
        </th>
      );
    }
    const active = sortBy === d.key;
    const ariaSort = active ? (sortDir === "asc" ? "ascending" : "descending") : "none";
    return (
      <th key={d.key} aria-sort={ariaSort} className={thClassForData(d)} {...thDropProps(d)}>
        <div className={innerRowClass}>
          {drag}
          <button type="button" className="admin-dt-sort-btn min-w-0" onClick={() => onSortColumn(d.key)}>
            <span>{d.label}</span>
            {active ? <span>{sortDir === "asc" ? " ▲" : " ▼"}</span> : null}
          </button>
        </div>
      </th>
    );
  };

  return (
    <div className={wrapperClassName}>
      <table className={tableClassName}>
        <thead>
          <tr>
            {hasActions && <th className="admin-dt-th admin-dt-th--actions">Actions</th>}
            {prependColumns.map((c) => (
              <th key={c.id} className={["admin-dt-th", c.thClassName].filter(Boolean).join(" ")} title={c.headerTitle}>
                {c.header}
              </th>
            ))}
            {visibleDefs.map((d) => renderSortableTh(d))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={colCount}>Loading…</td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={colCount}>{emptyText}</td>
            </tr>
          )}
          {!loading &&
            rows.map((row, idx) => {
              const extra = typeof getRowClassName === "function" ? getRowClassName(row) : "";
              const trClass = ["admin-dt-tr", extra].filter(Boolean).join(" ");
              return (
                <tr key={row._rowKey != null ? row._rowKey : row.id ?? idx} className={trClass}>
                  {hasActions && (
                    <td className="admin-dt-td admin-dt-td--actions">
                      <div className="admin-dt-actions-inner flex flex-row flex-nowrap items-center justify-center gap-1">
                        {renderActions(row)}
                      </div>
                    </td>
                  )}
                  {prependColumns.map((c) => (
                    <td key={c.id} className={["admin-dt-td", c.tdClassName].filter(Boolean).join(" ")}>
                      {c.renderCell(row)}
                    </td>
                  ))}
                  {visibleDefs.map((d) => {
                    if (typeof d.renderCell === "function") {
                      return (
                        <td
                          key={d.key}
                          className={["admin-dt-td", d.tdClassName].filter(Boolean).join(" ")}
                        >
                          {d.renderCell(row)}
                        </td>
                      );
                    }
                    const raw = pickRowValue(row, d);
                    const content =
                      raw == null || raw === "" ? (
                        <span className="text-gray-400">—</span>
                      ) : d.html ? (
                        <span dangerouslySetInnerHTML={{ __html: String(raw) }} />
                      ) : typeof raw === "object" ? (
                        JSON.stringify(raw)
                      ) : (
                        String(raw)
                      );
                    return (
                      <td
                        key={d.key}
                        className={["admin-dt-td", d.tdClassName].filter(Boolean).join(" ")}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
        </tbody>
      </table>
      {footer}
    </div>
  );
};

export default AdminDataTable;
